import { RefObject, useEffect, useRef } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import { CameraControls } from '@react-three/drei';
import { Group, MathUtils } from 'three';

/**
 * Manages camera-tracking rotation for the context preview scene.
 *
 * Normal mode: continuously rotates `groupRef` to follow the camera's azimuth
 * and polar angle (mirrors the Context.tsx useFrame formula).
 *
 * fullContext edit mode: drives `groupRef` from an invisible proxy group,
 * adding the live camera offset each frame. The proxy holds only the clean base
 * rotation (small angles, away from Euler gimbal-lock near ±π/2 on X), so
 * TransformControls operates stably. On mode entry the proxy is snapped to the
 * current base rotation so dragging starts from the correct orientation without
 * any visible jump.
 *
 * Must be called inside an R3F Canvas (uses `useThree` and `useFrame`).
 */
export function useContextCameraTracking(
  fullContextRotation: [number, number, number],
  transformMode: string | null | undefined,
  transformTarget: string | null | undefined
): {
  groupRef: RefObject<Group | null>;
  proxyRef: RefObject<Group | null>;
  initialX: number;
  initialY: number;
  initialZ: number;
} {
  const groupRef = useRef<Group>(null);
  const proxyRef = useRef<Group>(null);
  const { controls } = useThree() as { controls: CameraControls | null };

  const initialX = MathUtils.degToRad(fullContextRotation[0]);
  const initialY = MathUtils.degToRad(fullContextRotation[1]);
  const initialZ = MathUtils.degToRad(fullContextRotation[2]);

  const isFrozen = !!transformMode;

  // Always-current ref so the snap effect and useFrame read the latest base angles.
  const initialAnglesRef = useRef({ x: initialX, y: initialY, z: initialZ });
  initialAnglesRef.current = { x: initialX, y: initialY, z: initialZ };

  useFrame(() => {
    if (isFrozen && transformTarget === 'fullContext') {
      // Proxy drives the camera-tracking group: proxy holds clean base rotation,
      // camera offset is added on top each frame so models stay correctly oriented.
      if (proxyRef.current && groupRef.current && controls) {
        groupRef.current.rotation.x = proxyRef.current.rotation.x + Math.PI - controls.polarAngle;
        groupRef.current.rotation.y = proxyRef.current.rotation.y;
        groupRef.current.rotation.z = proxyRef.current.rotation.z + controls.azimuthAngle;
      }
    } else if (!isFrozen) {
      // Normal camera tracking (mirrors Context.tsx useFrame formula).
      if (groupRef.current && controls) {
        groupRef.current.rotation.z = initialZ + controls.azimuthAngle;
        groupRef.current.rotation.x = initialX + Math.PI - controls.polarAngle;
      }
    }
    // isFrozen && reference/subject: group stays at its last rotation.
  });

  // Snap proxy to base rotation on fullContext mode entry. Deps intentionally
  // exclude initialX/Y/Z — snap only on mode entry/change, not after each
  // drag-end re-render (which would reset the proxy mid-session).
  useEffect(() => {
    if (transformTarget === 'fullContext' && transformMode && proxyRef.current) {
      const { x, y, z } = initialAnglesRef.current;
      proxyRef.current.rotation.set(x, y, z);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transformTarget, transformMode]);

  return { groupRef, proxyRef, initialX, initialY, initialZ };
}
